import Head from 'next/head';
import type { GetStaticProps } from 'next';
import { PortableText } from '@portabletext/react';
import dayjs from 'dayjs';

import { fetchPublishedLineups } from '@api-service/predicted-lineups';
import type { PublishedLineup } from '@api-service/predicted-lineups';
import { fetchTeamFixturesPreview } from '@api-service/team-fixtures-preview';
import {
  ARTICLE_TOKENS,
  SCROLL_MARGIN_CLASS
} from '@components/article/design-tokens';
import { ArticleRail } from '@components/article/ArticleRail';
import { buildArticlePortableTextComponents } from '@components/article/portable-text-components';
import { ReadingProgressBar } from '@components/article/ReadingProgressBar';
import { TableOfContents } from '@components/article/TableOfContents';
import WideLayout from '@components/layout/wide';
import { MissingPropsRecovery } from '@components/missing-props-recovery';
import { ClubSection } from '@components/predicted-lineups/ClubSection';
import { GatedClubs } from '@components/predicted-lineups/GatedClubs';
import { usePremiumTeamSections } from '@hooks/usePremiumTeamSections';
import { hydrateArticleBlocks } from '@lib/article-blocks/hydrate';
import type { ArticleBlockData } from '@lib/article-blocks/types';
import { scaleShareImageDimensions } from '@lib/article-seo';
import { FFH_HOSTNAME, WEBSITE_NAME } from '@lib/constants';
import {
  buildClubFixtures,
  buildClubHeadings,
  collectHeadshotOptaIds,
  describeSections,
  findLineupForClub,
  flattenNotes,
  gatedClubsFor
} from '@lib/predicted-lineups/compose';
import type { ClubDescriptor } from '@lib/predicted-lineups/compose';
import { fetchFlaggedPlayers } from '@lib/predicted-lineups/availability';
import type { FlaggedPlayer } from '@lib/predicted-lineups/availability';
import { fetchPlayerFacts } from '@lib/predicted-lineups/player-facts';
import type { PlayerFactsMap } from '@lib/predicted-lineups/player-facts';
import {
  buildSanityImageUrl,
  getPredictedLineupsPage,
  PREDICTED_LINEUPS_PATH
} from '@lib/sanity';
import type {
  SanityPredictedLineupTeamSection,
  SanityPredictedLineupsPage
} from '@lib/sanity';

// Same freshness contract as articles: time-based ISR, expiring 404.
export const REVALIDATE_SECONDS = 600;
export const NOT_FOUND_REVALIDATE_SECONDS = 60;
const OG_IMAGE_WIDTH = 1200;

const PROPS_RELOAD_MARKER_KEY = 'ffh-predicted-lineups-props-reload';
const MISSING_PROPS_RUM_CONTEXT = 'predicted-lineups-missing-props';
const FALLBACK_PATH = '/';
const PRESS_CONFERENCE_TIME_FORMAT = 'ddd HH:mm';
const HEADLINES_HEADING = { id: 'key-headlines', text: 'Key headlines' };
const PRESS_CONFERENCES_HEADING = {
  id: 'press-conferences',
  text: 'Press conferences'
};

export type PredictedLineupsPageProps = {
  /** Always supplied by getStaticProps; see MissingPropsRecovery for why it is optional. */
  page?: SanityPredictedLineupsPage;
  lineups: PublishedLineup[];
  clubFixtures: ClubDescriptor[];
  playerFacts: PlayerFactsMap;
  /** FPL-flagged players at the clubs this payload may show: never a gated club's. */
  flaggedPlayers: FlaggedPlayer[];
  blockData: ArticleBlockData;
  isPreview?: boolean;
};

const PressConferences = ({
  entries
}: {
  entries: SanityPredictedLineupsPage['pressConferences'];
}) => {
  if (!entries || entries.length === 0) return null;
  return (
    <section
      id={PRESS_CONFERENCES_HEADING.id}
      className={`${SCROLL_MARGIN_CLASS} my-10`}
      data-cy="press-conferences"
    >
      <h2
        className={`m-0 mb-4 font-heading text-[22px] font-bold ${ARTICLE_TOKENS.ink}`}
      >
        {PRESS_CONFERENCES_HEADING.text}
      </h2>
      <ul
        className={`m-0 list-none divide-y rounded-[14px] border p-0 ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.surfaceTint}`}
      >
        {entries.map((entry) => (
          <li
            key={entry._key}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 py-3"
          >
            <span className={`w-12 font-bold ${ARTICLE_TOKENS.ink}`}>
              {entry.teamShortName}
            </span>
            {entry.manager && (
              <span className={ARTICLE_TOKENS.body}>{entry.manager}</span>
            )}
            {entry.time && (
              <span className={`ml-auto tabular-nums ${ARTICLE_TOKENS.muted}`}>
                {dayjs(entry.time).format(PRESS_CONFERENCE_TIME_FORMAT)}
              </span>
            )}
            {entry.note && (
              <span className={`w-full text-sm ${ARTICLE_TOKENS.bodyMuted}`}>
                {entry.note}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const PredictedLineupsPage = ({
  page,
  lineups,
  clubFixtures,
  playerFacts,
  flaggedPlayers,
  blockData,
  isPreview = false
}: PredictedLineupsPageProps) => {
  // The preview carries the draft's gated clubs; a published page never does,
  // so an entitled member fetches them here after the page loads.
  const premiumState = usePremiumTeamSections(isPreview ? undefined : page);

  if (!page) {
    return (
      <MissingPropsRecovery
        context={MISSING_PROPS_RUM_CONTEXT}
        reloadMarkerKey={PROPS_RELOAD_MARKER_KEY}
        fallbackPath={FALLBACK_PATH}
        errorMessage="Predicted lineups page props missing"
      />
    );
  }

  const metaTitle = page.seo?.metaTitle || `${page.title} | ${WEBSITE_NAME}`;
  const metaDescription = page.seo?.metaDescription || page.standfirst;
  const canonicalUrl =
    page.seo?.canonicalUrl || `${FFH_HOSTNAME}${PREDICTED_LINEUPS_PATH}`;
  // No featured image on this page, so without a share image set in the SEO
  // tab it falls back to the site default from DefaultSeo.
  const shareImage = page.seo?.ogImage;
  const ogImageUrl = shareImage?.asset
    ? buildSanityImageUrl(shareImage).width(OG_IMAGE_WIDTH).url()
    : undefined;
  const shareImageDimensions = scaleShareImageDimensions(
    OG_IMAGE_WIDTH,
    shareImage?.asset?.metadata?.dimensions
  );

  const freeSections = page.teams ?? [];
  // In the preview the draft's gated clubs render inline as ordinary sections.
  const previewPremiumSections: SanityPredictedLineupTeamSection[] = isPreview
    ? page.premiumTeams ?? []
    : [];
  const inlineSections = [...freeSections, ...previewPremiumSections];
  // Each side of the paywall reads A to Z, as the published page does.
  const inlineClubs = [
    ...describeSections(freeSections, clubFixtures),
    ...describeSections(previewPremiumSections, clubFixtures)
  ];
  const gatedClubs = gatedClubsFor(clubFixtures, inlineSections);

  const clubHeadings = buildClubHeadings([
    ...inlineClubs.map(({ club }) => club),
    ...gatedClubs
  ]);
  const inlineHeadings = clubHeadings.slice(0, inlineClubs.length);
  const gatedHeadings = clubHeadings.slice(inlineClubs.length);
  const tocHeadings = [
    ...(page.pressConferences?.length ? [PRESS_CONFERENCES_HEADING] : []),
    ...(page.keyHeadlines?.length ? [HEADLINES_HEADING] : []),
    ...clubHeadings
  ];
  const components = buildArticlePortableTextComponents(blockData, []);

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        <meta property="og:title" content={metaTitle} key="og:title" />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        {metaDescription && (
          <meta
            property="og:description"
            content={metaDescription}
            key="og:description"
          />
        )}
        {ogImageUrl && (
          <meta property="og:image" content={ogImageUrl} key="og:image" />
        )}
        {shareImageDimensions && (
          <meta
            property="og:image:width"
            content={String(shareImageDimensions.width)}
            key="og:image:width"
          />
        )}
        {shareImageDimensions && (
          <meta
            property="og:image:height"
            content={String(shareImageDimensions.height)}
            key="og:image:height"
          />
        )}
        {ogImageUrl && (
          <meta name="twitter:image" content={ogImageUrl} key="twitter:image" />
        )}
        {/* The draft preview must never be indexed; the published page is. */}
        {isPreview && <meta name="robots" content="noindex,nofollow" />}
      </Head>
      <ReadingProgressBar />
      <WideLayout>
        {isPreview && (
          <div
            className="bg-warning-300 px-4 py-2 text-center text-sm font-semibold text-neutral-900"
            data-cy="article-preview-banner"
          >
            Draft preview. Publish in the Studio to make this page live.
          </div>
        )}
        <div className="bg-white">
          <div className="mx-auto max-w-[1240px] px-4 pb-20 pt-10 sm:px-8 sm:pt-14 lg:grid lg:grid-cols-[minmax(0,1fr)_316px] lg:items-start lg:gap-16">
            <article className="article w-full max-w-[700px]">
              <header className="mb-8">
                <p
                  className={`m-0 mb-3 font-body text-[11px] uppercase tracking-[0.1em] ${ARTICLE_TOKENS.muted}`}
                  data-cy="predicted-lineups-gameweek"
                >
                  Gameweek {page.gameweek}
                </p>
                <h1
                  className={`m-0 font-heading text-[34px] font-bold leading-[1.1] sm:text-[42px] ${ARTICLE_TOKENS.ink}`}
                >
                  {page.title}
                </h1>
                {page.standfirst && (
                  <p className={`mb-0 mt-4 text-lg ${ARTICLE_TOKENS.body}`}>
                    {page.standfirst}
                  </p>
                )}
                {page._updatedAt && (
                  <p
                    className={`mb-0 mt-3 font-body text-[13px] ${ARTICLE_TOKENS.muted}`}
                  >
                    Updated {dayjs(page._updatedAt).format('ddd D MMM, HH:mm')}
                  </p>
                )}
              </header>
              <TableOfContents headings={tocHeadings} />
              {page.intro && page.intro.length > 0 && (
                <div className="content-body" data-cy="predicted-lineups-intro">
                  <PortableText value={page.intro} components={components} />
                </div>
              )}
              <PressConferences entries={page.pressConferences} />
              {page.keyHeadlines && page.keyHeadlines.length > 0 && (
                <section
                  id={HEADLINES_HEADING.id}
                  className={`${SCROLL_MARGIN_CLASS} my-10`}
                  data-cy="key-headlines"
                >
                  <h2
                    className={`m-0 mb-4 font-heading text-[22px] font-bold ${ARTICLE_TOKENS.ink}`}
                  >
                    {HEADLINES_HEADING.text}
                  </h2>
                  <div className="content-body">
                    <PortableText
                      value={page.keyHeadlines}
                      components={components}
                    />
                  </div>
                </section>
              )}
              {inlineClubs.map(({ section, club }, index) => (
                <ClubSection
                  key={section._key}
                  club={club}
                  heading={inlineHeadings[index]}
                  lineup={findLineupForClub(lineups, section.teamShortName)}
                  playerFacts={playerFacts}
                  flaggedPlayers={flaggedPlayers}
                  words={section}
                  blockData={blockData}
                />
              ))}
              <GatedClubs
                clubs={gatedClubs}
                headings={gatedHeadings}
                lineups={lineups}
                playerFacts={playerFacts}
                state={premiumState}
                returnTo={PREDICTED_LINEUPS_PATH}
              />
            </article>
            <ArticleRail headings={tocHeadings} />
          </div>
        </div>
      </WideLayout>
    </>
  );
};

const withFallback = async <T,>(
  label: string,
  read: Promise<T>,
  fallback: T
): Promise<T> => {
  try {
    return await read;
  } catch (error) {
    // The words still render when the league API is down; the pitch degrades
    // to its placeholder rather than the page failing to build.
    console.error(`[predicted-lineups] ${label} failed`, error);
    return fallback;
  }
};

/**
 * Everything the page needs from the league API for the document's gameweek.
 * FPL flags are read only for the clubs in `sections`, the ones this payload
 * may show, so the published page never carries a gated club's injuries.
 */
export const loadLeagueData = async (
  page: SanityPredictedLineupsPage,
  sections: SanityPredictedLineupTeamSection[]
) => {
  const [lineups, clubFixtures, flaggedPlayers] = await Promise.all([
    withFallback('lineups read', fetchPublishedLineups(page.gameweek), []),
    withFallback(
      'fixtures read',
      fetchTeamFixturesPreview({
        gameweekStart: page.gameweek,
        gameweekEnd: page.gameweek
      }).then((teams) => buildClubFixtures(teams, page.gameweek)),
      []
    ),
    withFallback(
      'flagged players read',
      fetchFlaggedPlayers(
        new Set(sections.map((section) => section.teamShortName))
      ),
      []
    )
  ]);
  const playerFacts = await withFallback(
    'playerFacts read',
    fetchPlayerFacts(collectHeadshotOptaIds(sections, lineups)),
    {}
  );
  return { lineups, clubFixtures, playerFacts, flaggedPlayers };
};

export const getStaticProps: GetStaticProps<
  PredictedLineupsPageProps
> = async () => {
  const page = await getPredictedLineupsPage();

  if (!page) {
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  const freeSections = page.teams ?? [];
  const [{ lineups, clubFixtures, playerFacts, flaggedPlayers }, blockData] =
    await Promise.all([
      loadLeagueData(page, freeSections),
      // Only the free clubs' notes are hydrated: the published query never
      // projects premiumTeams, so a static payload can never carry them.
      hydrateArticleBlocks([
        ...(page.intro ?? []),
        ...flattenNotes(freeSections)
      ])
    ]);

  return {
    props: {
      page,
      lineups,
      clubFixtures,
      playerFacts,
      flaggedPlayers,
      blockData
    },
    revalidate: REVALIDATE_SECONDS
  };
};

export default PredictedLineupsPage;
